import { ReactNode, useContext, useEffect, useMemo } from 'react';
import UserContext from '../../components/Context/UserContext';
import { useApolloClient, useQuery } from '@apollo/client';
import BasicEntityQuery from '../../graphql/queries/common/BasicEntityQuery.graphql';
import { IBasicEntityQueryResult, IBasicEntityQueryVariables } from '../../graphql/schema/p4types';
import CurrentUserQuery from '../../graphql/queries/system/CurrentUserQuery.graphql';

//In milliseconds - Currently set to 2 minutes.
const USER_CHECK_INTERVAL = 120000;

/**
 * A component to regularly poll for any changes to the current user record and update the application references to the user if it changes.
 * Expects a UserContext to exist above it to access the current user id.
 */
const UserChangeMonitor = ({ refetchUser }: { refetchUser: () => void }): ReactNode => {
  const { currentUser } = useContext(UserContext);
  const currentCompanyUserId = useMemo(() => currentUser?.companyUser?.id, [currentUser?.companyUser?.id]);
  const currentConcurrency = useMemo(
    () => currentUser?.companyUser?.concurrencyVersion,
    [currentUser?.companyUser?.concurrencyVersion]
  );
  const apolloClient = useApolloClient();

  const { data } = useQuery<IBasicEntityQueryResult, IBasicEntityQueryVariables>(BasicEntityQuery, {
    variables: {
      entityType: 'CompanyUser',
      id: currentCompanyUserId,
    },
    skip: !currentCompanyUserId,
    pollInterval: USER_CHECK_INTERVAL,
  });

  useEffect(() => {
    // Only need to refetch if fetched concurrency is greater - If it's somehow less, then the current user record is already 'more' up to date than this check
    //  (Highly unlikely timing scenario).
    if (data?.data && data.data.concurrencyVersion > currentConcurrency) {
      // Use refetch from "useCurrentUser" as this also updates the redux store.
      // (Apollo refetch will only update the user in Context, but many system processes reference user via Redux.).
      refetchUser();
    }
  }, [apolloClient, currentConcurrency, data?.data, refetchUser]);

  return null;
};

export default UserChangeMonitor;
